/*
 * ******************************************************************
 * Copyright (c) 2022-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, QueryList, ViewChildren } from "@angular/core";
import { ClrDatagridFilter, ClrDatagridFilterInterface, ClrPopoverService } from "@clr/angular";
import { Observable, Subject } from "rxjs";
import { L10nService } from "@vmw/ngx-vip";
import { EnumDescriptor } from "@shared/models/server-driven-filter.model";
import { takeUntil } from "rxjs/operators";

export class EnumItemModel {
    constructor(public label: string,
                public value: string) {
    }
}

// @formatter:off
@Component({
    standalone: false,
    selector: "dg-radio-filter",
    templateUrl: "./datagrid-radio-filter.component.html",
    styleUrls: ["./datagrid-radio-filter.component.scss"]
})
// @formatter:on
export class DatagridRadioFilter<T> implements ClrDatagridFilterInterface<T>, AfterViewInit, OnInit, OnDestroy {
    @ViewChildren("input", { read: ElementRef })
    private inputRef: QueryList<ElementRef>;

    @Input()
    public filterProperty: string;

    @Input()
    public sort: boolean = true;

    /**
     * Resource strings for enum values used to show in the filter.
     * @param value - array of strings
     */
    @Input()
    public set enumDescriptors(value: EnumDescriptor[]) {
        this._enumDescriptors = value;
    }

    public get enumDescriptors(): EnumDescriptor[] {
        return this._enumDescriptors;
    }

    /**
     * Preset filter selection.
     * @param value - array of strings
     */
    @Input()
    public set clrFilterValue(value: string) {
        this._initialValue = value;
    }

    public get clrFilterValue(): string {
        return this.selectedItem;
    }

    @Output()
    clrFilterValueChange: EventEmitter<string> = new EventEmitter();

    public items: Array<EnumItemModel>;
    public selectedItem: string;

    private _enumDescriptors: EnumDescriptor[];
    private _initialValue: string;
    private changesSubject: Subject<string> = new Subject();
    private destroySubject: Subject<void> = new Subject<void>();

    constructor(protected filterContainer: ClrDatagridFilter,
                private smartToggleService: ClrPopoverService,
                private ngZone: NgZone,
                protected l10nService: L10nService) {
        filterContainer.setFilter(this);
    }

    ngOnInit(): void {
        this.selectedItem = null;
        this.initFilter();
    }

    ngAfterViewInit(): void {
        this.smartToggleService.openChange
            .pipe(takeUntil(this.destroySubject))
            .subscribe(() => {
                this.ngZone.runOutsideAngular(() => {
                    requestAnimationFrame(() => {
                        if (this.inputRef.length > 0) {
                            this.inputRef.first.nativeElement.focus();
                        }
                    });
                });
            });
    }

    ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    /**
     * Indicates if the filter is currently active, (at least one input is set)
     */
    public isActive(): boolean {
        return this.selectedItem != null;
    }

    /**
     * The method is not used for server-driven datagrids, so no need to implement it.
     */
    public accepts(_item: T): boolean {
        return false;
    }

    public get state(): any {
        return {
            property: this.filterProperty,
            value: [this.selectedItem]
        }
    }

    /**
     * The Observable required as part of the Filter interface
     */
    @Output()
    public get changes(): Observable<string> {
        return this.changesSubject.asObservable();
    }

    public onRadioChecked(): void {
        this.changesSubject.next(this.selectedItem);
        this.clrFilterValueChange.emit(this.selectedItem);
        this.smartToggleService.open = false;
    }

    onButtonClick(): void {
        this.selectedItem = null;

        this.changesSubject.next(this.selectedItem);
        this.clrFilterValueChange.emit(this.selectedItem);
    }

    private presetFilter(): void {
        if (this._initialValue && this.items) {
            for (const item of this.items) {
                if (this._initialValue === item.value) {
                    this.selectedItem = item.value;
                }
            }

            this.changesSubject.next(this.selectedItem);
            this.clrFilterValueChange.emit(this.selectedItem);
        }
    }

    private initFilter(): void {
        this.items = new Array<EnumItemModel>();
        if (this.enumDescriptors) {
            for (const item of this.enumDescriptors) {
                let str: string;
                if (item.labelResourceId) {
                    str = (item.labelResourceId === "EMPTY" || !this.l10nService.isExistKey(item.labelResourceId)) ?
                          "" : this.l10nService.getMessage(item.labelResourceId);
                } else {
                    str = item.labelResourceValue;
                }
                if (str) {
                    this.items.push(new EnumItemModel(str, item.enumValue));
                }
            }
            if (this.sort) {
                this.items.sort((a, b) => a.label < b.label ? -1 : 1);
            }
        }

        this.presetFilter();
    }
}
